import type { Map as MapLibreMap } from 'maplibre-gl';

import type { RoutePlan } from '../api/types';
import { ROUTE_LINE_LAYERS } from '../components/MapView';

/**
 * Pictures of the route, taken from the map that is already on screen.
 *
 * There is no second renderer to ask. The map draws from vector tiles with a
 * style, a basemap archive and a dozen layers of our own on top, and a static
 * image service would produce a different map from the one somebody has been
 * reading — different colours, no stops, no exclusion zones. So the export
 * borrows the real one: frames the route, waits for it to settle, and reads
 * the canvas.
 *
 * It does move the view, briefly. The camera is put back where it was, which
 * is less rude than it sounds because the whole thing takes a second or two
 * behind a spinner.
 */

export interface RouteImages {
  /** The whole round, framed. Null when the map could not be read. */
  overview: string | null;
  /** One per leg, in leg order. A null entry is a leg that would not frame. */
  legs: (string | null)[];
}

/**
 * How many legs get a picture of their own.
 *
 * Each one is a camera move, a wait and a PNG, so an operation with forty
 * stops would spend half a minute and tens of megabytes making a file nobody
 * asked to be that big. Past this the directions stand on their own.
 */
const MAX_LEG_IMAGES = 12;

/** Waits for the map to finish drawing, or gives up rather than hanging. */
function settled(map: MapLibreMap, timeoutMs = 4000): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      map.off('idle', finish);
      resolve();
    };
    map.on('idle', finish);
    setTimeout(finish, timeoutMs);
  });
}

/**
 * The canvas as a PNG, read in the one moment it can be read.
 *
 * WebGL throws the drawing buffer away as soon as the frame is composited,
 * so `toDataURL` from anywhere else comes back blank. The buffer is still
 * there inside a `render` handler, which is why the read is done by asking
 * for one more frame and grabbing it from inside.
 *
 * `preserveDrawingBuffer` would make this unnecessary, but it is a map
 * constructor option and react-map-gl does not pass it through — and turning
 * it on would cost every frame of ordinary panning a copy, to serve an export
 * almost nobody runs.
 */
async function shoot(
  map: MapLibreMap,
  plan: RoutePlan,
  only: number[] | null = null,
): Promise<string | null> {
  const raw = await new Promise<string | null>((resolve) => {
    let done = false;
    const finish = (value: string | null) => {
      if (done) return;
      done = true;
      resolve(value);
    };

    // `void` because maplibre's `once` is typed as returning a promise when
    // it is called without a listener; with one it returns the map.
    void map.once('render', () => {
      try {
        finish(map.getCanvas().toDataURL('image/png'));
      } catch {
        finish(null);
      }
    });
    map.triggerRepaint();

    // A map that never renders again must not hold the whole export up.
    setTimeout(() => finish(null), 3000);
  });

  if (!raw) return null;

  return withStops(map, plan, raw, only);
}

/**
 * Shows one leg of the route and hides the rest, or puts them all back.
 *
 * The route is one source with a feature per leg, so this is a filter rather
 * than a redraw. Without it a leg's picture carries the whole round's line
 * doubling back across the frame, and the one leg the page is about is the
 * hardest thing on it to find.
 */
function focusLeg(map: MapLibreMap, legIndex: number | null) {
  for (const id of ROUTE_LINE_LAYERS) {
    if (!map.getLayer(id)) continue;
    map.setFilter(id, legIndex === null ? null : ['==', ['get', 'legIndex'], legIndex]);
  }
}

/** Every coordinate of the route, as a bounding box. */
function boundsOf(points: [number, number][]): [[number, number], [number, number]] | null {
  if (points.length === 0) return null;
  let west = points[0][0];
  let south = points[0][1];
  let east = west;
  let north = south;
  for (const [lng, lat] of points) {
    if (lng < west) west = lng;
    if (lng > east) east = lng;
    if (lat < south) south = lat;
    if (lat > north) north = lat;
  }

  /*
    A box with no width is a leg that starts and ends on the same spot, which
    fitBounds answers by zooming to the maximum. Padded out to something a
    person can recognise.
  */
  const pad = 0.0015;
  if (east - west < pad) { west -= pad; east += pad; }
  if (north - south < pad) { south -= pad; north += pad; }

  return [[west, south], [east, north]];
}

/** S, E, or the stop's number - the marks the map and the itinerary use. */
function markFor(index: number, total: number): string {
  if (index === 0) return 'S';
  if (index === total - 1) return 'E';

  return String(index);
}

/** Loads a data URL so it can be drawn onto a 2D canvas. */
function load(dataUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/**
 * Puts the stops back into the picture.
 *
 * The markers on screen are HTML sitting over the map, not something the GL
 * context draws, so a canvas read comes back with the route and none of the
 * pins: a line through a city with nothing to say where it stops. They are
 * drawn on here instead, from the projection the map is using at the moment
 * of the shot, with the same marks the itinerary uses.
 */
async function withStops(
  map: MapLibreMap,
  plan: RoutePlan,
  shot: string,
  only: number[] | null,
): Promise<string> {
  const gl = map.getCanvas();
  const image = await load(shot);
  if (!image) return shot;

  const canvas = document.createElement('canvas');
  canvas.width = gl.width;
  canvas.height = gl.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return shot;

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  // The GL canvas is sized in device pixels; `project` answers in CSS ones.
  const scale = gl.clientWidth > 0 ? gl.width / gl.clientWidth : 1;
  const r = 12 * scale;

  plan.stops.forEach((stop, i) => {
    // A leg's picture carries the two stops it runs between and no others:
    // the rest belong to other pages and only make this one harder to read.
    if (only && !only.includes(i)) return;

    const at = map.project([stop.lng, stop.lat]);
    const x = at.x * scale;
    const y = at.y * scale;
    /*
      Off this frame, so not drawn.

      A leg's picture is framed on that leg, and the stops of the rest of the
      round are nowhere near it. Clamping them to the edge would be inventing
      a position.
    */
    if (x < -r || y < -r || x > canvas.width + r || y > canvas.height + r) return;

    const first = i === 0;
    const last = i === plan.stops.length - 1;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = first ? '#2e7d32' : last ? '#c62828' : '#1d6f6a';
    ctx.fill();
    ctx.lineWidth = 2 * scale;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${12 * scale}px Helvetica, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(markFor(i, plan.stops.length), x, y + 0.5 * scale);
  });

  return canvas.toDataURL('image/png');
}

/**
 * Takes the pictures. Puts the camera back when it is done, whatever happens.
 */
export async function captureRouteImages(
  map: MapLibreMap,
  plan: RoutePlan,
): Promise<RouteImages> {
  const was = {
    center: map.getCenter(),
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
  };

  const images: RouteImages = { overview: null, legs: [] };

  try {
    const everything = [
      ...plan.stops.map((s) => [s.lng, s.lat] as [number, number]),
      ...plan.legs.flatMap((l) => l.geometry),
    ];

    const whole = boundsOf(everything);
    if (whole) {
      map.fitBounds(whole, { padding: 48, animate: false });
      await settled(map);
      images.overview = await shoot(map, plan);
    }

    for (let i = 0; i < plan.legs.length; i += 1) {
      if (i >= MAX_LEG_IMAGES) {
        images.legs.push(null);
        continue;
      }
      const leg = plan.legs[i];
      const box = boundsOf(leg?.geometry ?? []);
      if (!box || !leg) {
        images.legs.push(null);
        continue;
      }
      focusLeg(map, i);
      map.fitBounds(box, { padding: 40, animate: false });
       
      await settled(map);
       
      images.legs.push(await shoot(map, plan, [leg.fromIndex, leg.toIndex]));
    }
  } finally {
    /*
      Put back whatever happened above.

      A failed capture that left the map showing one leg of a round would be a
      worse bug than a missing picture, and it would outlive the export.
    */
    focusLeg(map, null);
    map.jumpTo(was);
  }

  return images;
}
